import * as THREE from 'three';
import { Kit, place } from '../seasons/kit.ts';
import { HIP_X, HIP_Y } from './villagers.ts';
import { begin, done, type BaseOpts, type Model } from './types.ts';

/*
 * The wayfarers of Orla's patrol (La Quemadura 1, docs/QUEMADURA.md): the ones who map the titan, back from the Burn
 * wounded. A long travel cloak and hood in dusty grey-green, a leather pack on the back with a map case strapped over
 * it, ash up to the knees, a pale bandage (round the head or an arm) and a short staff at the belt. Built like the
 * villagers (one vertex-coloured body, the legs apart, instanced) so a whole patrol is a couple of draws, but never
 * dressed like them: the pack and the map case read from the table camera.
 * Feet at y = 0, facing +Z, a hero's height (about 1.75).
 */

export const WAYFARER_LOOKS = 4;

const SKINS = ['#d9b498', '#b98e70', '#a07658', '#e2c3a8'];
/** Dusty travel cloaks: grey-green, ash grey, a faded moss, a dark slate (no gold, no beige). */
const CLOAKS = ['#5c6352', '#6a6a66', '#55604e', '#4a5258'];
const HAIR = ['#2a211c', '#4a3626', '#6e6a64', '#1c1c20'];
const LEATHER = '#5a4430';
const DARK_LEATHER = '#3a2a1c';
const BANDAGE = '#e6e1d8';

const pick = <T>(a: readonly T[], n: number): T => a[((n % a.length) + a.length) % a.length] as T;

/** A wayfarer's body (no legs) as one vertex-coloured geometry; `look` picks the colours and where the bandage is. */
export const wayfarerBody = (look: number): THREE.BufferGeometry => {
  const k = new Kit();
  const skin = pick(SKINS, look);
  const cloak = pick(CLOAKS, look);
  const tunic = pick(['#4e4a42', '#5a5246', '#46423c', '#504c48'], look + 1);
  const hair = pick(HAIR, look);
  const body = new THREE.Matrix4();
  const up = (x: number, y: number, z: number, yaw = 0, sc: number | THREE.Vector3 = 1, tx = 0, tz = 0): THREE.Matrix4 => place(x, y, z, yaw, sc, tx, tz);
  // Hips and a belt with a pouch and a short staff thrust through it.
  k.add(new THREE.CylinderGeometry(0.15, 0.14, 0.14, 8), DARK_LEATHER, body, up(0, HIP_Y + 0.04, 0));
  k.add(new THREE.BoxGeometry(0.09, 0.1, 0.05), LEATHER, body, up(0.14, HIP_Y + 0.02, 0.1));
  k.add(new THREE.CylinderGeometry(0.014, 0.014, 0.6, 5), '#6b5238', body, up(-0.16, HIP_Y + 0.05, 0.05, 0, 1, 0.5));
  // The torso and the long cloak over it, open at the front, to the knee.
  k.add(new THREE.CylinderGeometry(0.19, 0.16, 0.48, 8), tunic, body, up(0, HIP_Y + 0.3, 0));
  k.add(new THREE.CylinderGeometry(0.21, 0.34, 0.92, 10, 1, true, Math.PI * 0.62, Math.PI * 1.76), cloak, body, up(0, HIP_Y + 0.1, -0.01));
  // The hood fallen back.
  k.add(new THREE.SphereGeometry(0.14, 8, 6, 0, Math.PI * 2, 0, Math.PI * 0.5).scale(1.1, 0.7, 1).rotateX(-1.2), cloak, body, up(0, HIP_Y + 0.62, -0.12));
  // The pack: a leather box on the back, a bedroll on top, the map case strapped across it.
  k.add(new THREE.BoxGeometry(0.3, 0.36, 0.16), LEATHER, body, up(0, HIP_Y + 0.32, -0.24));
  k.add(new THREE.CylinderGeometry(0.07, 0.07, 0.34, 8).rotateZ(Math.PI / 2), '#7a7466', body, up(0, HIP_Y + 0.55, -0.24));
  k.add(new THREE.CylinderGeometry(0.04, 0.04, 0.6, 8).rotateZ(0.7), '#6a4a2e', body, up(0, HIP_Y + 0.38, -0.34));
  for (const s of [-1, 1]) k.add(new THREE.CylinderGeometry(0.045, 0.045, 0.04, 8).rotateZ(0.7), DARK_LEATHER, body, up(s * 0.2, HIP_Y + 0.38 + s * 0.17, -0.34));
  // Straps over the shoulders.
  for (const s of [-1, 1]) k.add(new THREE.BoxGeometry(0.04, 0.42, 0.02), DARK_LEATHER, body, up(s * 0.1, HIP_Y + 0.36, 0.165, 0, 1, 0.05));
  // Arms in the cloak's sleeves; one bound in a pale bandage on half of them.
  for (const sd of [-1, 1]) {
    const arm = up(sd * 0.21, HIP_Y + 0.44, 0, 0, 1, 0.05, sd * 0.12);
    k.add(new THREE.CylinderGeometry(0.055, 0.065, 0.42, 6).translate(0, -0.2, 0), cloak, body, arm);
    if (sd > 0 && look % 2 === 1) k.add(new THREE.CylinderGeometry(0.068, 0.068, 0.12, 6).translate(0, -0.24, 0), BANDAGE, body, arm);
    k.add(new THREE.SphereGeometry(0.05, 6, 5).translate(0, -0.43, 0), skin, body, arm);
  }
  // Head, neck, face, hair; a bandage round the brow on the other half.
  const head = up(0, HIP_Y + 0.68, 0.02);
  k.add(new THREE.CylinderGeometry(0.05, 0.06, 0.08, 6), skin, body, up(0, HIP_Y + 0.58, 0));
  k.add(new THREE.SphereGeometry(0.115, 10, 8), skin, body, head);
  k.add(new THREE.BoxGeometry(0.03, 0.04, 0.03).translate(0, -0.01, 0.11), skin, body, head);
  k.add(new THREE.SphereGeometry(0.122, 10, 8, 0, Math.PI * 2, 0, Math.PI * 0.5), hair, body, head);
  if (look % 2 === 0) k.add(new THREE.TorusGeometry(0.118, 0.022, 4, 14).rotateX(Math.PI / 2).translate(0, 0.04, 0), BANDAGE, body, head);
  // A soot smear of the Burn on the cloak.
  k.add(new THREE.BoxGeometry(0.16, 0.2, 0.01), '#2e2c2a', body, up(0.08, HIP_Y - 0.05, 0.3, 0, 1, -0.2));
  const mesh = k.build(new THREE.MeshBasicMaterial(), 'wayfarer');
  const geo = (mesh as THREE.Mesh).geometry;
  geo.computeBoundingSphere();
  return geo;
};

/** One leg from the hip down (pivot at the origin, hanging along −Y), the boot grey with ash to the knee. */
export const wayfarerLeg = (): THREE.BufferGeometry => {
  const k = new Kit();
  const m = new THREE.Matrix4();
  k.add(new THREE.CylinderGeometry(0.06, 0.055, (HIP_Y - 0.08) * 0.5, 6).translate(0, -(HIP_Y - 0.08) * 0.25, 0), '#3e3c38', m);
  k.add(new THREE.CylinderGeometry(0.066, 0.062, (HIP_Y - 0.08) * 0.5, 6).translate(0, -(HIP_Y - 0.08) * 0.75, 0), '#8a8580', m);
  k.add(new THREE.BoxGeometry(0.11, 0.1, 0.19).translate(0, -HIP_Y + 0.05, 0.03), '#6e6a66', m);
  const mesh = k.build(new THREE.MeshBasicMaterial(), 'wayfarerLeg');
  return (mesh as THREE.Mesh).geometry;
};

/** The patrol for the gallery: every look in a row (a static model, standing). */
export const wayfarers = (o?: BaseOpts): Model => {
  const c = begin(o, 1.2, 81);
  const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, flatShading: true });
  const leg = wayfarerLeg();
  for (let i = 0; i < WAYFARER_LOOKS; i++) {
    const x = (i - (WAYFARER_LOOKS - 1) / 2) * 0.7;
    const b = new THREE.Mesh(wayfarerBody(i), mat);
    b.position.x = x;
    c.g.add(b);
    for (const s of [-1, 1]) {
      const l = new THREE.Mesh(leg, mat);
      l.position.set(x + s * HIP_X, HIP_Y, 0);
      c.g.add(l);
    }
  }
  return done(c, 1.8);
};
